import React from "react";
import getDisplayName from "react-display-name";
import App from "next/app";
import { initialisePrivateEnvConfig, initialisePublicEnvConfig, } from "../internal/store.js";
/**
 * Higher-order function that leverages `getInitialProps` to sync Environment Config between the server and client.
 * @param Comp
 *
 * @example ```tsx
 * // pages/_app.tsx
 * import React from "react";
 * import type { AppProps } from "next/app";
 * import { withEnvConfig } from "@catchoftheday/env-config";
 *
 * function App({ Component, pageProps }: AppProps) {
 * 	return <Component {...pageProps} />;
 * }
 *
 * export default withEnvConfig(App); // <-- Wrap your app with `withEnvConfig`
 * ```
 */
export function withEnvConfig(Comp, options = {}) {
    const { globalTarget = globalThis } = options;
    const WithEnvConfig = ({ envConfig, ...props }) => {
        // Conditional is here is purely to satisfy TS; Next.js will ensure the `envConfig` prop is always defined.
        // `initialiseEnvConfig` will throw if it is not.
        if (envConfig) {
            // This must be called before `App` is rendered to ensure the config is available.
            initialisePublicEnvConfig(envConfig, globalTarget);
        }
        return <Comp {...props}/>;
    };
    WithEnvConfig.displayName = `withEnvConfig(${getDisplayName(Comp)})`;
    WithEnvConfig.getInitialProps = async (appContext) => {
        let envConfig = null;
        // Only run on the server
        if (typeof window === "undefined") {
            // Lazy-load the config to avoid accidentally sending to the client.
            const { publicEnvConfig, privateEnvConfig } = (await import("../env-config.config.js")).loadEnvConfig();
            // To ensure the config is available for downstream `getInitialProps` calls, we must initialise it here
            // (in addition to within the `WithEnvConfig` as `getInitialProps` is SSR-only).
            initialisePublicEnvConfig(publicEnvConfig, globalTarget);
            initialisePrivateEnvConfig(privateEnvConfig, globalTarget);
            envConfig = publicEnvConfig;
        }
        // Invoke downstream `getInitialProps`
        // > 📝 Support for App-specific config has been dropped as it would've required extensive changes to how Apollo Client is initialised.
        const [appProps, compProps] = await Promise.all([
            App.getInitialProps(appContext),
            Comp.getInitialProps?.(appContext) ?? {},
        ]);
        return {
            ...appProps,
            ...compProps,
            envConfig,
        };
    };
    return WithEnvConfig;
}
//# sourceMappingURL=withEnvConfig.jsx.map